import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { HelpCircle, Send, Sparkles, X } from 'lucide-react';
import { useAuth } from '../../AuthContext';
import { NexaMessage, TypingBubble } from './NexaMessage';
import './nexa.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GREETING = { from: 'assistant', text: "Hi, I'm Agent Nexa. How can I help you today?" };

export default function AgentNexaWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, busy]);
  if (!user) return null;

  const openPanel = () => { setMessages([GREETING]); setInput(''); setOpen(true); };
  const send = async (e) => {
    e?.preventDefault();
    const q = input.trim();
    if (!q || busy) return;
    setInput('');
    setMessages((m) => [...m, { from: 'user', text: q }]);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/help/ask`, { question: q });
      setMessages((m) => [...m, { from: 'assistant', text: data.answer, queryId: data.query_id, video: data.video, status: data.status, cached: data.cached }]);
    } catch (err) {
      setMessages((m) => [...m, { from: 'assistant', text: err?.response?.data?.detail || 'Something went wrong. Please try again.', status: 'error' }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      {!open && (
        <button onClick={openPanel} data-testid="nexa-help-button" aria-label="Help & Support"
          className="fixed z-[60] right-4 bottom-[148px] md:bottom-20 md:right-5 w-12 h-12 rounded-full bg-[#0F1D3A] text-white shadow-[0_10px_30px_-8px_rgba(15,29,58,0.55)] flex items-center justify-center hover:bg-[#1B2A4E] hover:scale-105 active:scale-95 transition-transform nexa-fab">
          <HelpCircle className="w-6 h-6" />
        </button>
      )}
      {open && <div className="fixed inset-0 z-[69] bg-slate-900/20 md:bg-transparent md:pointer-events-none" onClick={() => setOpen(false)} />}
      <aside data-testid="nexa-chat-panel" aria-hidden={!open}
        className={`fixed z-[70] top-0 right-0 h-full w-full md:w-[380px] bg-[#F6F8FC] border-l border-slate-200 shadow-2xl flex flex-col transition-transform duration-300 ease-out ${open ? 'translate-x-0' : 'translate-x-full pointer-events-none'}`}>
        <header className="flex items-center gap-3 px-4 py-3.5 bg-[#0F1D3A] text-white">
          <span className="w-8 h-8 rounded-full bg-[#22D3EE]/15 border border-[#22D3EE]/40 flex items-center justify-center"><Sparkles className="w-4 h-4 text-[#22D3EE]" /></span>
          <div className="flex-1 min-w-0">
            <h2 className="text-[15px] font-semibold leading-tight" data-testid="nexa-panel-title">Agent Nexa</h2>
            <p className="text-[11px] text-slate-300">AudiNexa Help &amp; Support</p>
          </div>
          <button onClick={() => setOpen(false)} data-testid="nexa-close-button" aria-label="Close"
            className="p-1.5 rounded-md hover:bg-white/10 transition-colors"><X className="w-5 h-5" /></button>
        </header>
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="nexa-messages">
          {messages.map((m, i) => <NexaMessage key={i} msg={m} idx={i} onPickRole={(q) => { setInput(q); inputRef.current?.focus(); }} />)}
          {busy && <TypingBubble />}
          <div ref={endRef} />
        </div>
        <form onSubmit={send} className="p-3 border-t border-slate-200 bg-white flex gap-2">
          <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask how to do something…" maxLength={1000}
            data-testid="nexa-input" disabled={busy}
            className="flex-1 text-[13px] rounded-full border border-slate-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#0891B2]/40 focus:border-[#0891B2] disabled:bg-slate-50" />
          <button type="submit" disabled={busy || !input.trim()} data-testid="nexa-send-button"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#0891B2] hover:bg-[#0E7490] disabled:opacity-40 text-white text-[13px] font-semibold px-4 transition-colors">
            <Send className="w-4 h-4" /> Send
          </button>
        </form>
      </aside>
    </>
  );
}
